<template>
  <BaseDrawer
    :model-value="isOpen"
    :title="$t('commerce.storeCartDrawer.title')"
    max-width-class="sm:max-w-md"
    full-screen-on-mobile
    @close="closeCart"
  >
    <!-- Empty State for Store Cart -->
    <div
      v-if="storeItems.length === 0"
      class="py-8 text-center"
    >
      <div
        class="bg-surface-level-100 dark:bg-surface-level-800 flex h-16 items-center justify-center mb-4 mx-auto rounded-full w-16"
      >
        <ShoppingCartIcon class="h-8 text-content-level-400 w-8" />
      </div>

      <h4 class="dark:text-content-inverse font-medium mb-2 text-content-level-900 text-sm">
        {{ $t('commerce.storeCartDrawer.yourCartIsEmpty') }}
      </h4>

      <p class="dark:text-content-level-400 mb-4 text-content-level-500 text-sm">
        {{ $t('commerce.storeCartDrawer.addSomeDeliciousItemsToGetStarted') }}
      </p>
    </div>

    <!-- Store Cart Content (only show when cart has items) -->
    <div v-else>
      <!-- Delivery Location/Order Type -->
      <div class="mb-3">
        <div class="flex items-center justify-between">
          <h3 class="dark:text-content-inverse font-medium text-content-level-900 text-sm">
            {{ orderTypeDisplay }}
          </h3>

          <button
            type="button"
            class="cursor-pointer dark:text-primary hover:text-primary text-primary text-sm"
            @click="openLocationFilter"
          >
            {{ $t('commerce.storeCartDrawer.change') }}
          </button>
        </div>

        <p
          v-if="eventStoreName"
          class="dark:text-content-level-400 text-content-level-500 text-sm w-4/5"
        >
          {{ eventStoreName }}
        </p>

        <p class="dark:text-content-level-400 text-content-level-500 text-xs w-4/5">
          {{ eventLocation }}
        </p>
      </div>
      <!-- Delivery Date/Time -->
      <div class="mb-6">
        <div class="flex items-center justify-between">
          <h3 class="dark:text-content-inverse font-medium text-content-level-900 text-sm">
            {{ $t('commerce.storeCartDrawer.date') }}
          </h3>

          <button
            type="button"
            class="cursor-pointer dark:text-primary hover:text-primary text-primary text-sm"
            @click="openDateTimeFilter"
          >
            {{ $t('commerce.storeCartDrawer.change') }}
          </button>
        </div>

        <p class="dark:text-content-level-400 text-content-level-500 text-sm">
          {{ deliveryDate }}
        </p>
      </div>

      <!-- Cart Items -->
      <div class="mb-6">
        <div class="space-y-0">
          <div
            v-for="(item, index) in storeItems"
            :key="item.id"
            class="-mx-5 dark:hover:bg-surface-level-700/30 duration-200 hover:bg-surface-level-50 px-5 py-4 transition-colors"
            :class="{
              'border-b border-border-level-100 dark:border-border-level-700':
                index < storeItems.length - 1
            }"
          >
            <CartItemSummary
              :image="item.menu_item?.image ?? undefined"
              :name="item.item_name"
              :total="formatCurrency(item.total_price)"
              :special-instructions="item.special_instructions ?? undefined"
              :customizations="item.customizations ?? []"
              :group-key-prefix="`${item.id}`"
              :quantity-label="getItemQuantityLabel(item)"
              :quantity="item.quantity"
            >
              <template #actions>
                <div class="flex gap-3 items-center">
                  <button
                    type="button"
                    class="cursor-pointer dark:text-primary-400 disabled:cursor-not-allowed disabled:opacity-60 gap-2 hover:text-primary-dark inline-flex items-center text-primary text-sm"
                    @click="() => handleEditItem(item)"
                  >
                    {{ $t('commerce.storeCartDrawer.edit') }}
                  </button>

                  <button
                    type="button"
                    class="cursor-pointer dark:text-danger-400 disabled:cursor-not-allowed disabled:opacity-60 gap-2 hover:text-danger-500 inline-flex items-center text-danger-600 text-sm"
                    :disabled="removingItemId.provided && removingItemId.value === item.id"
                    @click="() => removeItem(item.id)"
                  >
                    <span
                      v-if="removingItemId.provided && removingItemId.value === item.id"
                      class="loading loading-spinner loading-xs"
                      aria-hidden="true"
                    />{{ $t('commerce.storeCartDrawer.remove') }}
                  </button>
                </div>
              </template>
            </CartItemSummary>
          </div>
        </div>
      </div>

      <!-- Utensils -->
      <!--
        <div class="mb-6">
        <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-content-level-900 dark:text-content-inverse">Utensils</h3>
        <button class="text-sm text-primary hover:text-primary dark:text-primary">Change</button>
        </div>
        <p class="text-sm text-content-level-500 dark:text-content-level-400">{{ cartStore.utensils }}</p>
        </div>
      -->

      <!-- Price Breakdown (same order as cart/checkout) -->
      <div :class="orderType === OrderType.DINE_IN ? 'mb-2' : 'mb-6'">
        <div class="space-y-2">
          <div class="flex justify-between text-sm">
            <span class="dark:text-content-level-400 text-content-level-600">{{
              $t('commerce.storeCartDrawer.foodBeverage')
            }}</span>

            <span class="dark:text-content-inverse text-content-level-900">{{
              formatCurrency(storeSubtotal)
            }}</span>
          </div>

          <div
            v-if="hasDeliveryFee"
            class="flex justify-between text-sm"
          >
            <span class="dark:text-content-level-400 text-content-level-600">{{
              $t('commerce.storeCartDrawer.deliveryFee')
            }}</span>

            <span class="dark:text-content-inverse text-content-level-900">{{
              formatCurrency(deliveryFee)
            }}</span>
          </div>

          <div
            v-if="hasApplicationFee"
            class="flex justify-between text-sm"
          >
            <span class="dark:text-content-level-400 text-content-level-600">{{
              $t('commerce.storeCartDrawer.serviceFee')
            }}</span>

            <span class="dark:text-content-inverse text-content-level-900">{{
              formatCurrency(applicationFeeValue)
            }}</span>
          </div>

          <div
            v-if="hasTax"
            class="flex justify-between text-sm"
          >
            <span class="dark:text-content-level-400 text-content-level-600">{{
              $t('commerce.storeCartDrawer.taxes')
            }}</span>

            <span class="dark:text-content-inverse text-content-level-900">{{
              formatCurrency(tax)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Gratuity Section -->
      <div class="mb-6">
        <template v-if="orderType === OrderType.DINE_IN">
          <div class="flex justify-between text-sm">
            <span class="dark:text-content-level-400 text-content-level-600">{{
              $t('commerce.storeCartDrawer.gratuityIncluded')
            }}</span>

            <span class="dark:text-content-inverse text-content-level-900">{{
              formatCurrency(tipAmount)
            }}</span>
          </div>
        </template>

        <template v-else>
          <div class="flex items-center justify-between mb-3">
            <h3 class="dark:text-content-inverse font-medium text-content-level-900 text-sm">
              {{ $t('commerce.storeCartDrawer.gratuity') }}
            </h3>
          </div>

          <GratuitySelector
            :model-value="tipSelection"
            :subtotal="storeSubtotal"
            @update:model-value="selectTip"
          />

          <div class="flex justify-between mt-2 text-sm">
            <span class="dark:text-content-level-400 text-content-level-600">{{
              $t('commerce.storeCartDrawer.gratuityAmount')
            }}</span>

            <span class="dark:text-content-inverse text-content-level-900">{{
              formatCurrency(tipAmount)
            }}</span>
          </div>
        </template>
      </div>

      <!-- Total -->
      <div class="border-border-level-200 border-t dark:border-border-level-700 mb-4 pt-4">
        <div class="flex font-semibold justify-between text-lg">
          <span class="dark:text-content-inverse text-content-level-900">{{
            $t('commerce.storeCartDrawer.total')
          }}</span>

          <span class="dark:text-content-inverse text-content-level-900">{{
            formatCurrency(total)
          }}</span>
        </div>

        <OrderMinimumNotice
          :minimum="minimumOrder"
          :shortfall="minimumShortfall"
        />

        <p class="dark:text-content-level-400 mt-1 text-content-level-500 text-right text-sm">
          <span v-if="perPersonCost.provided">{{
            $t('commerce.storeCartDrawer.costPerson', { cost: perPersonCost.value })
          }}</span>

          <span v-else>{{ $t('commerce.storeCartDrawer.addYourGuestCountForCostPer') }}</span>

          <button
            type="button"
            class="cursor-pointer dark:hover:text-primary-300 dark:text-primary-400 focus:outline-none font-medium hover:text-primary-dark ml-1 text-primary underline"
            @click="openEventDetailsFilter"
          >
            {{ $t('commerce.storeCartDrawer.change') }}
          </button>
        </p>
      </div>
    </div>

    <template
      v-if="storeItems.length > 0"
      #footer
    >
      <button
        type="button"
        class="btn btn-active btn-primary disabled:cursor-not-allowed disabled:opacity-70 text-content-inverse w-full"
        :disabled="isCheckoutDisabled"
        @click="checkout"
      >
        <span
          v-if="checkoutLoading"
          class="inline-flex items-center"
        >
          <span
            class="loading loading-spinner loading-xs mr-2"
            aria-hidden="true"
          />{{ $t('commerce.storeCartDrawer.checkingAvailability') }}</span
        >

        <span v-else>
          {{ $t('commerce.storeCartDrawer.checkout') }}
        </span>
      </button>
    </template>
  </BaseDrawer>
</template>

<script setup lang="ts">
import { ShoppingCartIcon } from '@heroicons/vue/24/outline'
import { toRef } from 'vue'
import BaseDrawer from '~/components/shared/dialogs/BaseDrawer.vue'
import CartItemSummary from '~/components/shared/CartItemSummary.vue'
import GratuitySelector from '~/components/shared/GratuitySelector.vue'
import OrderMinimumNotice from '~/components/shared/OrderMinimumNotice.vue'
import { useStoreCartDrawer } from '~/composables/useStoreCartDrawer'
import type { CartItemView } from '~/services/api/generated'
import { useCurrencyFormat } from '~/composables/useCurrencyFormat'
import type { GratuitySelection } from '~/types/GratuitySelection'
import { OrderType } from '~/enums'

const props = withDefaults(
  defineProps<{
    /** Controls whether the cart drawer is visible. */
    isOpen?: boolean
    /** Store associated with the cart. */
    store?: Record<string, unknown> | null
    /** Indicates that the current store is unavailable. */
    isStoreUnavailable?: boolean
    /** Message explaining store unavailability. */
    storeUnavailableMessage?: string | null
    /**
     * Runs the host's own canonical availability check instead of duplicating it; a `false`
     * result means the host already showed the failure. Omitted hosts fall back to their own check.
     */
    checkAvailability?: (() => Promise<boolean>) | null
  }>(),
  { checkAvailability: null, store: null, storeUnavailableMessage: null }
)

const emit = defineEmits<{
  close: []
  openDatetimeFilter: []
  openMobileEventDetails: []
  openLocationFilter: []
  openEventDetailsFilter: []
  availabilityFailed: [payload: AvailabilityFailurePayload]
  editItem: [item: CartItemView]
}>()

interface AvailabilityFailurePayload {
  message: () => string
  data?: unknown
}

const {
  storeItems,
  deliveryDate,
  storeSubtotal,
  hasDeliveryFee,
  deliveryFee,
  hasApplicationFee,
  applicationFeeValue,
  hasTax,
  tax,
  tipAmount,
  total,
  tipSelection,
  minimumOrder,
  minimumShortfall,
  orderType,
  checkoutLoading,
  removingItemId,
  perPersonCost,
  orderTypeDisplay,
  eventStoreName,
  eventLocation,
  isCheckoutDisabled,
  getItemQuantityLabel,
  closeCart,
  checkout,
  removeItem
} = useStoreCartDrawer({
  checkAvailability: toRef(() => props.checkAvailability),
  isOpen: toRef(() => props.isOpen),
  isStoreUnavailable: toRef(() => props.isStoreUnavailable),
  onAvailabilityFailed: (failure) => {
    emit('availabilityFailed', {
      data: 'data' in failure ? failure.data : null,
      message: failure.message
    })
  },
  onClose: () => {
    emit('close')
  },
  onGuestCountRequired: () => {
    openEventDetailsFilter()
  },
  store: toRef(() => props.store),
  storeUnavailableMessage: toRef(() => props.storeUnavailableMessage)
})

const formatCurrency = useCurrencyFormat()

const selectTip = (selection: GratuitySelection) => {
  tipSelection.value = selection
}

const handleEditItem = (item: CartItemView) => {
  closeCart()
  emit('editItem', item)
}

const openDateTimeFilter = () => {
  // Check if we're on mobile (screen width < 768px)
  if (window.innerWidth < 768) {
    emit('openMobileEventDetails')
  } else {
    emit('openDatetimeFilter')
  }
}

// Open event details only (guest count / number of attendees) — not date or location
const openEventDetailsFilter = () => {
  if (window.innerWidth < 768) {
    emit('openMobileEventDetails')
  } else {
    emit('openEventDetailsFilter')
  }
}

const openLocationFilter = () => {
  // Check if we're on mobile (screen width < 768px)
  if (window.innerWidth < 768) {
    emit('openMobileEventDetails')
  } else {
    emit('openLocationFilter')
  }
}
</script>
